import React, { FC, useEffect, useRef, useState } from 'react'
import { Search, Typography } from '@equinor/eds-core-react'
import {
    SensorCardContainer,
    CardBody,
    CardHeading,
    ContentRow,
    CardTextWrapper,
    CardIcon,
    DescriptionButton,
    DescriptionPanel,
    DescriptionText,
    InfoGrid,
    InfoLabel,
    InfoValue,
    AtexValue,
    AtexIcon,
    RangeRow,
    ProductButtonRow,
    ProductPageButton,
    HeaderContainer,
    CardContainer,
    BackgroundContainer,
    HeaderRow,
    HeaderTextColumn,
    ToolbarColumn,
    ToolbarRow,
    MobileFilterBar,
    MobileToolbar,
} from '@/components/styles/PortfolioStyles'
import { StyledLink } from '@/components/styles/AboutStyles'
import { useLocation, useNavigate } from 'react-router-dom'
import { useGetDevices } from '@/core/hooks-and-services/devicesService'
import { SensorImage, SensorImagePopup } from '@/components/sensorImage/SensorImage'
import { useAtom } from 'jotai'
import FilterButton from '@/components/filterButton/FilterButton'
import PortfolioFilterDialog from '@/features/portfolio/components/PortfolioFilterDialog'
import PortfolioChipRow from '@/features/portfolio/components/PortfolioChipRow'
import {
    selectedFilterPortfolioAtexAtom,
    selectedFilterPortfolioIpRatingAtom,
    selectedFilterPortfolioMeasureTypeAtom,
    selectedFilterPortfolioProtocolAtom,
    selectedFilterPortfolioSearchAtom,
    selectedFilterPortfolioVendorAtom,
} from '@/core/store/globalStore'
import { filterPortfolioDevices, sortPortfolioDevices } from '@/features/portfolio/utils/filterPortfolioDevices'
import { useWindowWidth } from '@/hooks/useWindowWidth'
import type { KeyboardEvent, MouseEvent } from 'react'

const ATEX_WARNING_VALUES = ['no atex', 'pending']
const isAtexWarning = (atex: string | null | undefined) =>
    ATEX_WARNING_VALUES.includes((atex ?? '').trim().toLowerCase())

interface SensorCardProps {
    deviceKey?: string | null
    imageSrc: string
    title: string
    protocol?: string | null
    deviceMeasurementType?: string | null
    atex?: string | null
    ipRating?: string | null
    lifespan?: string | null
    range?: string | null
    description: string
    productPageLink: string
    isDefaultExpanded: boolean
}

const SensorCard = React.forwardRef<HTMLDivElement, SensorCardProps>(
    (
        {
            deviceKey,
            imageSrc,
            title,
            protocol,
            deviceMeasurementType,
            atex,
            ipRating,
            lifespan,
            range,
            description,
            productPageLink,
            isDefaultExpanded,
        },
        ref
    ) => {
        const [isExpanded, setIsExpanded] = useState(isDefaultExpanded)
        const encodedDeviceKey = encodeURIComponent(deviceKey || '')

        const [isPopupOpen, setIsPopupOpen] = useState(false)
        const [selectedImage, setSelectedImage] = useState<string | null>(null)

        const location = useLocation()
        const navigate = useNavigate()

        const handleImageCardClick = (imageSrc: string) => {
            setSelectedImage(imageSrc)
            setIsPopupOpen(true)
        }

        const handleCloseImageCardPopup = () => {
            setIsPopupOpen(false)
            setSelectedImage(null)
        }

        const toggleExpanded = () => {
            setIsExpanded(!isExpanded)
            const otherExpandedSensors = location.pathname
                .split('/')
                .slice(2)
                .filter((pathSegment) => pathSegment && pathSegment !== encodedDeviceKey)
            const expandedSensors = isExpanded ? otherExpandedSensors : [...otherExpandedSensors, encodedDeviceKey]
            navigate(['/portfolio', ...expandedSensors].join('/'), { replace: true })
        }

        const handleCardClick = (event: MouseEvent<HTMLDivElement>) => {
            const action =
                event.target instanceof Element
                    ? event.target.closest('button, a, [role="button"], [tabindex="0"], [data-card-action]')
                    : null
            if (action && action !== event.currentTarget) {
                return
            }
            toggleExpanded()
        }

        const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return
            event.preventDefault()
            toggleExpanded()
        }

        const gridEntries = [
            { label: 'Type', value: deviceMeasurementType?.trim() || '—' },
            { label: 'Protocol', value: protocol?.trim() || '—' },
            { label: 'ATEX', value: atex?.trim() || '—' },
            { label: 'Lifespan', value: lifespan?.trim() || '—' },
            { label: 'IP rating', value: ipRating?.trim() || '—' },
        ]

        return (
            <SensorCardContainer
                ref={ref}
                $isExpanded={isExpanded}
                onClick={handleCardClick}
                onKeyDown={handleCardKeyDown}
                role="button"
                tabIndex={0}
                aria-label={`${isExpanded ? 'Hide' : 'Show'} description for ${title}`}
            >
                <CardBody $isExpanded={isExpanded}>
                    <CardHeading variant="h6">{title}</CardHeading>
                    <ContentRow>
                        <SensorImage imageSrc={imageSrc} onClick={() => handleImageCardClick(imageSrc)} />
                        {isPopupOpen && selectedImage && (
                            <SensorImagePopup imageSrc={selectedImage} onClose={handleCloseImageCardPopup} />
                        )}
                        <CardTextWrapper>
                            <InfoGrid>
                                {gridEntries.map(({ label, value }) => (
                                    <React.Fragment key={label}>
                                        <InfoLabel>{label}</InfoLabel>
                                        {label === 'ATEX' && isAtexWarning(atex) ? (
                                            <AtexValue>
                                                <AtexIcon name="errorOutlined" size={16} />
                                                {value}
                                            </AtexValue>
                                        ) : (
                                            <InfoValue>{value}</InfoValue>
                                        )}
                                    </React.Fragment>
                                ))}
                            </InfoGrid>
                        </CardTextWrapper>
                    </ContentRow>
                    <DescriptionButton
                        onClick={toggleExpanded}
                        variant={isExpanded ? 'contained' : 'ghost'}
                        color="primary"
                    >
                        Description
                        <CardIcon name={isExpanded ? 'chevron_up' : 'chevron_down'} />
                    </DescriptionButton>
                </CardBody>
                {isExpanded && (
                    <DescriptionPanel>
                        <DescriptionText>{description}</DescriptionText>
                        {range && (
                            <RangeRow>
                                <InfoLabel style={{ color: '#3d3d3d' }}>Range:</InfoLabel>
                                <InfoValue>{range}</InfoValue>
                            </RangeRow>
                        )}
                        <ProductButtonRow>
                            <a href={productPageLink} target="_blank" rel="noopener noreferrer">
                                <ProductPageButton variant="ghost">
                                    Product page
                                    <CardIcon name="external_link" />
                                </ProductPageButton>
                            </a>
                        </ProductButtonRow>
                    </DescriptionPanel>
                )}
            </SensorCardContainer>
        )
    }
)

const Portfolio: FC = () => {
    const { data } = useGetDevices()
    const location = useLocation()
    const windowWidth = useWindowWidth()
    const isMobile = windowWidth <= 1024

    const cardRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
    const filterAnchorRef = useRef<HTMLDivElement>(null)

    const [filterOpen, setFilterOpen] = useState(false)
    const [search, setSearch] = useAtom(selectedFilterPortfolioSearchAtom)
    const [measureType] = useAtom(selectedFilterPortfolioMeasureTypeAtom)
    const [atex] = useAtom(selectedFilterPortfolioAtexAtom)
    const [protocol] = useAtom(selectedFilterPortfolioProtocolAtom)
    const [ipRating] = useAtom(selectedFilterPortfolioIpRatingAtom)
    const [vendor] = useAtom(selectedFilterPortfolioVendorAtom)
    const activeFilterCount = [measureType, atex, protocol, ipRating, vendor].filter((v) => v !== null).length

    const expandedDevices = location.pathname.split('/').slice(2).filter(Boolean)

    useEffect(() => {
        if (expandedDevices && expandedDevices.length === 1) {
            const cardId = expandedDevices[0]
            const cardElement = cardRefs.current[cardId]
            if (cardElement) {
                cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
        }
    }, [data])

    const portfolioDevices = (data ?? []).filter((device) => device.portfolio)
    const visibleDevices = sortPortfolioDevices(
        filterPortfolioDevices(portfolioDevices, { measureType, atex, protocol, ipRating, vendor }, search)
    )

    const searchInput = (
        <Search
            aria-label="Search"
            placeholder="Search"
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
        />
    )

    return (
        <>
            <BackgroundContainer>
                <HeaderRow>
                    <HeaderTextColumn>
                        <HeaderContainer>
                            <Typography variant="h1">Portfolio</Typography>
                            <Typography>
                                TDI has qualified and developed underlying infrastructure for a wide range of IIoT
                                sensors. The sensors below can be easily ordered, installed, and integrated into the
                                Equinor system.
                            </Typography>
                            <Typography>
                                In case you need a sensor not mentioned here, we encourage you to{' '}
                                <StyledLink href="mailto:gm_TDI_IIoT_Sensors@equinor.com">contact us.</StyledLink>
                            </Typography>
                        </HeaderContainer>
                    </HeaderTextColumn>
                    {!isMobile && (
                        <ToolbarColumn>
                            <ToolbarRow>
                                {searchInput}
                                <div ref={filterAnchorRef}>
                                    <FilterButton onClick={() => setFilterOpen(true)} count={activeFilterCount} />
                                </div>
                            </ToolbarRow>
                            <PortfolioChipRow />
                        </ToolbarColumn>
                    )}
                </HeaderRow>

                {isMobile && (
                    <MobileToolbar>
                        {searchInput}
                        <PortfolioChipRow />
                    </MobileToolbar>
                )}

                <CardContainer>
                    {visibleDevices.map((device) => {
                        if (
                            !device ||
                            !device.url ||
                            !device.displayName ||
                            !device.vendor ||
                            !device.description ||
                            !device.productPageLink
                        )
                            return null

                        const isExpanded = expandedDevices.includes(encodeURIComponent(device.key as string))

                        return (
                            <SensorCard
                                key={encodeURIComponent(device.key as string)}
                                deviceKey={device.key}
                                imageSrc={device.url}
                                title={`${device.vendor} ${device.displayName}`}
                                protocol={device.protocol}
                                deviceMeasurementType={device.deviceMeasurementType}
                                atex={device.atex}
                                ipRating={device.ipRating}
                                lifespan={device.lifespan}
                                range={device.range}
                                description={device.description}
                                productPageLink={device.productPageLink}
                                isDefaultExpanded={isExpanded}
                                ref={(refValue) => {
                                    cardRefs.current[encodeURIComponent(device.key as string)] = refValue
                                }}
                            />
                        )
                    })}
                </CardContainer>

                {isMobile && (
                    <MobileFilterBar>
                        <FilterButton onClick={() => setFilterOpen(true)} count={activeFilterCount} />
                    </MobileFilterBar>
                )}

                <PortfolioFilterDialog
                    showMenu={filterOpen}
                    onClose={() => setFilterOpen(false)}
                    devices={portfolioDevices}
                    anchor={isMobile ? null : filterAnchorRef.current}
                />
            </BackgroundContainer>
        </>
    )
}

export default Portfolio
